import { Box, CircularProgress, Paper, Typography } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import { type FC, useState } from "react"

import type { useAssociatedTestTemplateColumns } from "../../hooks"
import { AssociatedTestTemplateFormDialogContainer } from "../AssociatedTestTemplateFormDialogContainer/AssociatedTestTemplateFormDialogContainer"
import AssociatedTestTemplatesTable from "../AssociatedTestTemplatesTable/AssociatedTestTemplatesTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import type { AerosTableReorderReturn } from "@/components/Tables/Aeros/types/table"
import { PermissionActionType } from "@/constants/permissions"
import type { AssociatedTestTemplate } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"

interface AssociatedTestTemplatesTableSectionProps {
  productId: number
  associatedTestTemplates: AssociatedTestTemplate[]
  columns: ReturnType<typeof useAssociatedTestTemplateColumns>
  isFetching: boolean
  onRefresh: () => void
  onReorder: (data: AerosTableReorderReturn<AssociatedTestTemplate>) => void
  companyId?: string | null
  locationId?: string | null
}

export const AssociatedTestTemplatesTableSection: FC<AssociatedTestTemplatesTableSectionProps> = ({
  productId,
  associatedTestTemplates,
  columns,
  isFetching,
  onRefresh,
  onReorder,
  companyId,
  locationId,
}) => {
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)
  const { hasPermissions } = usePermissionManager()
  const canMaintainTestTemplates = hasPermissions([PermissionActionType.TEST_TEMPLATE_MAINTENANCE])
  const hasTestTemplates = associatedTestTemplates.length > 0

  const handleFormDialogOpen = () => {
    setIsFormDialogOpen(true)
  }

  const handleFormDialogClose = () => {
    setIsFormDialogOpen(false)
  }

  const handleFormDialogSubmit = () => {
    setIsFormDialogOpen(false)
    onRefresh()
  }

  return (
    <>
      <Paper variant='outlined' elevation={0} className='border-gray-300 p-6' data-testid='test-templates-section'>
        <Box className='flex items-center justify-between gap-4'>
          <Typography variant='subtitle1' component='h3' className='text-ml text-gray-900'>
            Quality Test Template
          </Typography>
          {hasTestTemplates && canMaintainTestTemplates && (
            <Button
              color='primary'
              appearance='outlined'
              startIcon={<AddOutlined />}
              onClick={handleFormDialogOpen}
              data-testid='add-test-template-button'
            >
              Associate Test Template
            </Button>
          )}
        </Box>
        <Box className='mt-6' data-testid='test-templates'>
          {hasTestTemplates ? (
            <AssociatedTestTemplatesTable
              data={associatedTestTemplates}
              isLoading={isFetching}
              columns={columns}
              onReorder={onReorder}
            />
          ) : isFetching ? (
            <Box className='flex justify-center py-6'>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <InformationMissingAlert
              title='Add a Test Template for this product'
              action={
                canMaintainTestTemplates && (
                  <Button
                    color='inherit'
                    size='small'
                    appearance='outlined'
                    className='text-primary'
                    startIcon={<AddOutlined />}
                    onClick={handleFormDialogOpen}
                    data-testid='add-test-template-button'
                  >
                    Associate Test Template
                  </Button>
                )
              }
            />
          )}
        </Box>
      </Paper>
      {isFormDialogOpen && (
        <AssociatedTestTemplateFormDialogContainer
          productId={productId}
          associatedTestTemplate={null}
          onClose={handleFormDialogClose}
          onSubmit={handleFormDialogSubmit}
          companyId={companyId}
          locationId={locationId}
        />
      )}
    </>
  )
}
